import * as React from "react";
import {cx} from "emotion";

import "./mailing-class.scss";

import {enableCanadaMailing} from "@Common/logic/enable-canada-mailing";
import {getMinQty}           from "@Common/logic/template-options/get-min-qty";
import {cs}                  from "@Common/react/chain-services";
import {consumeContext}      from "@Common/react/context";
import {keyed}               from "@Common/react/keyed";
import {InformationBadge}    from "@Common/ui-components/heavy/common/design2/information-badge/information-badge";
import {Radio}               from "@Common/ui-components/heavy/common/design2/radio/radio";
import {Tooltip2}            from "@Common/ui-components/heavy/common/design2/tooltip-2/tooltip-2";

export const MailingClass = ({paperPrices, className, templateOptions, item, state, forcedStandard}) => cs(
    consumeContext("session"),
    consumeContext("company"),
    ["canadaMailing", (_, next) => enableCanadaMailing({templateOptions, next})],
    ["hasEddm", ({session, company}, next) => next(
        item?.programType !== "Scheduled Campaigns" &&
        (!session.data.branch || session.data.branch.addressListTypes?.includes("saturation")) &&
        company?.prefs?.addressListTypes?.includes("saturation")
    )],
    ({hasEddm, canadaMailing}) => {
        const mailingClassKeys = Object.keys(templateOptions.mailingClasses);
        const nDMMinimumQuantity = (paperPrices && getMinQty(paperPrices)) ?? 500;
        return (
            <div className={cx("mailing-class-3j6", className)}>
                {getMailingClassDefinitions({hasEddm, canadaMailing, mailingClassKeys, nDMMinimumQuantity})
                    .map((mailingClass) => cs(
                        keyed(mailingClass.key),
                        ["tooltip", (_, next) => mailingClassKeys.includes(mailingClass.key) || !mailingClass.disabledToolTip ? next() : Tooltip2({
                            getMessage: mailingClass.disabledToolTip,
                            next,
                        })],
                        ({tooltip}) => {
                            const disabled = (!mailingClassKeys.includes(mailingClass.key)) || forcedStandard && !["3", "5"].includes(mailingClass.key);
                            return (
                                <div
                                    className={cx("class", {disabled})}

                                    onMouseEnter={tooltip?.onMouseEnter}
                                    onMouseLeave={tooltip?.onMouseLeave}
                                    onClick={tooltip?.onClick}
                                >
                                    <div className="radio">
                                        {Radio({
                                            selected: state.value === mailingClass.key,
                                            onClick: () => !disabled && state.onChange(mailingClass.key),
                                            xdQaName: mailingClass.xdQaName,
                                        })}
                                    </div>
                                    <div className="text">

                                        <div className="label" xd-qa-type="label" xd-qa-name={mailingClass.xdQaName}>
                                            {mailingClass.label}
                                        </div>
                                        {tooltip && (
                                            <div
                                                className="tooltip"
                                                ref={tooltip?.ref}
                                            >
                                                {tooltip?.render()}
                                            </div>
                                        )}
                                        <div
                                            className="description"
                                            xd-qa-type="label"
                                            xd-qa-name={mailingClass.xdQaName + " Description"}
                                        >
                                            {mailingClass.description}
                                        </div>
                                        {mailingClass?.description2 && <div
                                            className="label"
                                            xd-qa-type="label"
                                            xd-qa-name={mailingClass.xdQaName + " Description 2"}
                                        >
                                            {mailingClass.description2}
                                        </div>}

                                    </div>
                                </div>
                            );
                        },
                    ))
                }
            </div>
        );
    },
);

const firstClassMailTooltip = () => "Best option for time-sensitive mailings";
const standardMailTooltip = () => "Standard Mail is not forwarded or returned if undeliverable";
const nDMMailTooltip = () => "Discounted rate for delivering your mailed piece to every door in your selected USPS carrier routes, and will include Recipient Name";
const canadianLettermailTooltip = () => "Best option for time-sensitive mailings. Mail will not be returned if undeliverable";
const canadianAdmailTooltip = () => "Mail will not be returned if undeliverable";
const eddmMailTooltip = () => "An enhanced version of USPS EDDM® that includes recipient names on most mailing addresses";
const standardDisabledToolTip = () => "Standard Mail is not available for this template.";
const firstClassDisabledToolTip = () => "First Class Mail is not available for this template.";

const getMailingClassDefinitions = ({hasEddm, canadaMailing, mailingClassKeys, nDMMinimumQuantity}) => {
    if (mailingClassKeys.includes("5")) {
        return [{
            key: "5",
            label: <>
                {"Neighborhood Direct Mail"}
                {mailingClassKeys.includes("5") && InformationBadge({
                    getTooltip: nDMMailTooltip,
                    xdQaName: 'NDM Mail tooltip'
                })}
            </>,
            xdQaName: "Neighborhood Direct Mail",
            description: "USPS delivery time is 5 - 15 business days",
            description2: `Note: Neighborhood Direct Mail requires a minimum qty of ${nDMMinimumQuantity} pieces.`,
        }];
    }
    return [
        ...[{
            key: "1",
            label: (canadaMailing) ? (
                <>
                    {"Canadian Lettermail"}
                    {InformationBadge({
                        getTooltip: canadianLettermailTooltip,
                        xdQaName: 'Canadian Lettermail tooltip'
                    })}
                </>
            ) : (
                <>
                    {"First Class Mail"}
                    {InformationBadge({
                        getTooltip: firstClassMailTooltip,
                        xdQaName: 'First Class Mail tooltip'
                    })}
                </>
            ),
            xdQaName: (canadaMailing) ? "Canadian Lettermail" : "First Class Mail",
            description: (canadaMailing) ? "Canada Post delivery time is 5 - 8 business days" : "USPS delivery time is 3 - 5 business days",
            disabledToolTip: firstClassDisabledToolTip,
        }],
        {
            key: "3",
            label: canadaMailing ? (
                <>
                    {"Canadian Admail"}
                    {mailingClassKeys.includes("3") && InformationBadge({
                        getTooltip: canadianAdmailTooltip,
                        xdQaName: 'Canadian Admail tooltip'
                    })}</>
            ) : (
                <>
                    {"Standard Mail"}
                    {mailingClassKeys.includes("3") && InformationBadge({
                        getTooltip: standardMailTooltip,
                        xdQaName: 'Standard Mail tooltip'
                    })}
                    {hasEddm && " or EDDM ELITE"}
                    {hasEddm && mailingClassKeys.includes("3") && InformationBadge({
                        getTooltip: eddmMailTooltip,
                        xdQaName: 'EDDM ELITE tooltip'
                    })}
                </>
            ),
            xdQaName: canadaMailing ? (
                "Canadian Admail"
            ) : (
                "Standard Mail" + (hasEddm ? " or EDDM ELITE" : "")
            ),

            description: (canadaMailing) ? "Canada Post delivery time is 7 - 14 business days" : "USPS delivery time is 5 - 15 business days",
            disabledToolTip: standardDisabledToolTip,
        },
    ].filter(mailingClass=>mailingClass);
}
